import { useState } from "react";
import { Plus, Trash2, Pencil, Check } from "lucide-react";
import DietEditor from "@/components/lab/DietEditor";
import { makeDiet, dietLetter } from "@/lib/diet";
import { toast } from "sonner";

export default function PianoTab({ diets, setDiets, foods, onCreateFood, activeDiet, setActiveDiet, macroGrams }) {
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const idx = Math.min(activeDiet, diets.length - 1);
  const diet = diets[idx];

  const addDiet = () => {
    const d = makeDiet(`Dieta ${dietLetter(diets.length)}`);
    setDiets([...diets, d]);
    setActiveDiet(diets.length);
    toast.success(`${d.nome} creata`);
  };

  const removeDiet = (dietId) => {
    if (diets.length <= 1) { toast.error("Deve restare almeno una dieta"); return; }
    setDiets(diets.filter((d) => d.id !== dietId));
    setActiveDiet(0);
  };

  const saveRename = (dietId) => {
    setDiets(diets.map((d) => (d.id === dietId ? { ...d, nome: renameVal.trim() || d.nome } : d)));
    setRenaming(null);
  };

  const updateActiveDiet = (newDiet) =>
    setDiets(diets.map((d) => (d.id === newDiet.id ? newDiet : d)));

  return (
    <div className="space-y-5 pb-24" data-testid="tab-piano">
      {/* Floating diet selector bar — follows the scroll, always visible */}
      <div className="fixed bottom-4 left-1/2 z-40 flex max-w-[92vw] -translate-x-1/2 items-center gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-[hsl(var(--card))]/95 px-3 py-2.5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.9)] backdrop-blur-md" data-testid="diet-selector-bar">
        {diets.map((d, i) => (
          <div key={d.id} className={`group flex shrink-0 items-center gap-1 rounded-lg border px-1 py-1 transition-colors ${i === idx ? "border-primary/50 bg-primary/10" : "border-white/10 bg-black/20"}`}>
            {renaming === d.id ? (
              <>
                <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename(d.id)} className="w-28 rounded bg-black/40 px-2 py-1 text-sm outline-none" data-testid={`rename-diet-input-${i}`} />
                <button onClick={() => saveRename(d.id)} className="p-1 text-primary" data-testid={`rename-save-${i}`}><Check className="h-4 w-4" /></button>
              </>
            ) : (
              <>
                <button onClick={() => setActiveDiet(i)} className={`px-2 py-1 text-sm font-semibold ${i === idx ? "text-primary" : "text-muted-foreground"}`} data-testid={`diet-tab-${i}`}>{d.nome}</button>
                <button onClick={() => { setRenaming(d.id); setRenameVal(d.nome); }} className="p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100" data-testid={`rename-diet-${i}`}><Pencil className="h-3.5 w-3.5" /></button>
                {diets.length > 1 && (
                  <button onClick={() => removeDiet(d.id)} className="p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100" data-testid={`delete-diet-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </>
            )}
          </div>
        ))}
        <button onClick={addDiet} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-dashed border-primary/40 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10" data-testid="add-diet-btn">
          <Plus className="h-4 w-4" /> Aggiungi Dieta
        </button>
      </div>

      {diet && (
        <>
          <h3 className="font-display text-lg font-bold text-primary" data-testid="active-diet-title">{diet.nome}</h3>
          <DietEditor diet={diet} onChange={updateActiveDiet} foods={foods} onCreateFood={onCreateFood} macroGrams={macroGrams} />
        </>
      )}
    </div>
  );
}
